<template>
  <vs-dialog v-model="isOpen" width="500px" prevent-close @close="close">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-plus-circle"></i> ເຕີມເງິນກະເປົ໋າ
      </h4>
    </template>

    <div class="dialog-content">
      <div class="branch-info">
        <span class="label">ສາຂາ:</span>
        <span class="value">{{ branch.name }} ({{ branch.prefix }})</span>
      </div>

      <div class="form-group">
        <label class="label">ປະເພດກະເປົ໋າ <span class="required">*</span></label>
        <div class="select">
          <select v-model="form.wallet_type" :disabled="isSubmitting">
            <option value="FREIGHT">ຄ່າຂົນສົ່ງ</option>
            <option value="COD">ເກັບເງິນປາຍທາງ (COD)</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label class="label">ຈຳນວນເງິນ (LAK) <span class="required">*</span></label>
        <input
            class="input"
            type="text"
            inputmode="numeric"
            :disabled="isSubmitting"
            :value="formatNumberInput(form.amount)"
            @input="onAmountInput($event.target.value)"
            @blur="$event.target.value = formatNumberInput(form.amount)"
            placeholder="ຕ່ຳສຸດ 1,000 LAK"
        >
        <p class="help-text" v-if="form.amount && !isAmountOverCeiling">
          {{ formatCurrency(form.amount) }}
        </p>
        <p class="help-text help-error" v-if="isAmountOverCeiling">
          ເກີນຈຳນວນສູງສຸດ ({{ formatCurrency(maxTopupAmount) }})
        </p>
      </div>

      <div class="form-group">
        <label class="label">ລາຍລະອຽດ <span class="required">*</span></label>
        <textarea
            class="textarea"
            v-model="form.description"
            rows="3"
            placeholder="ເຫດຜົນໃນການເຕີມເງິນ..."
        ></textarea>
      </div>

      <div class="form-group">
        <label class="label">ສະລິບການໂອນ</label>
        <div class="file-upload">
          <input
              type="file"
              ref="fileInput"
              accept="image/*,.pdf"
              @change="handleFileSelect"
              style="display: none"
          >
          <vs-button
              flat
              @click="$refs.fileInput.click()"
              :disabled="isSubmitting"
          >
            <i class="fal fa-upload"></i> ເລືອກໄຟລ໌
          </vs-button>
          <span v-if="selectedFile" class="file-name">
            {{ selectedFile.name }}
            <i v-if="!isSubmitting" class="fal fa-times" @click="clearFile" style="cursor: pointer; margin-left: 8px;"></i>
          </span>
        </div>
        <p class="help-text">ຮອງຮັບ: JPG, PNG, PDF (ສູງສຸດ 5MB)</p>
      </div>

      <div v-if="previewUrl" class="slip-preview">
        <img v-if="isImageFile" :src="previewUrl" alt="Slip preview">
        <div v-else class="pdf-indicator">
          <i class="fal fa-file-pdf"></i> PDF File
        </div>
      </div>

      <!-- [4.2] Explicit confirm: restate branch + wallet + amount before the money moves. -->
      <div v-if="showConfirm" class="notification is-warning topup-confirm">
        <p><strong>ກວດສອບການເຕີມເງິນ</strong></p>
        <p>ສາຂາ: <strong>{{ branch.name }} ({{ branch.prefix }})</strong></p>
        <p>ປະເພດກະເປົ໋າ: <strong>{{ form.wallet_type === 'FREIGHT' ? 'ຄ່າຂົນສົ່ງ' : 'ເກັບເງິນປາຍທາງ (COD)' }}</strong></p>
        <p>ຈຳນວນເງິນ: <strong>{{ formatCurrency(form.amount) }}</strong></p>
      </div>

      <div v-if="submitError" class="notification is-warning submit-error">
        <p>{{ submitError }}</p>
        <p v-if="submitErrorRequestId" class="error-ref">ref: {{ submitErrorRequestId }}</p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <template v-if="!showConfirm">
          <vs-button flat @click="close" :disabled="isSubmitting">
            ຍົກເລີກ
          </vs-button>
          <vs-button
              @click="requestConfirm"
              :disabled="isSubmitting || !isFormValid"
          >
            <i class="fal fa-check"></i> ເຕີມເງິນ
          </vs-button>
        </template>
        <template v-else>
          <vs-button flat @click="showConfirm = false" :disabled="isSubmitting">
            ກັບຄືນ
          </vs-button>
          <vs-button
              @click="submit"
              :loading="isSubmitting"
              :disabled="isSubmitting"
          >
            <i class="fal fa-check"></i> ຢືນຢັນການເຕີມເງິນ
          </vs-button>
        </template>
      </div>
    </template>
  </vs-dialog>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency as _formatCurrency, formatNumberInput } from '@/services/walletFormatters';

// [4.2 / ADM-F3] Client-side bounds for a single manual top-up. Max mirrors Go's configured
// ceiling (MAX_TRANSACTION_AMOUNT, prod 300,000,000 LAK since raise-topup-max-to-300m) so a
// fat-finger is rejected before the upload, not after. The server stays authoritative — if its
// env is lower, the request gets a localized 400 showing the real bounds.
const MIN_TOPUP_AMOUNT = 1000;
const MAX_TOPUP_AMOUNT = 300000000;

export default {
  name: 'TopUpDialog',
  props: {
    branch: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isOpen: true,
      isSubmitting: false,
      submitError: null,
      submitErrorRequestId: null,
      // [B1] Stable idempotency key for THIS top-up intent. Generated once per dialog instance and
      // reused across resubmits — a human retry after a 60s upload timeout (or an ambiguous error
      // where Go may have committed) must send the SAME key, or Go can't dedup and the branch is
      // credited TWICE for one bank transfer. Rotated (rotateIdempotencyKey) when the amount /
      // wallet type / slip changes, so a genuinely different top-up isn't deduped against this one.
      idempotencyKey: `topup-${crypto.randomUUID()}`,
      // [4.2] Two-step submit: the first click stages a confirm panel (restating branch + amount);
      // the actual Go call only fires after explicit confirmation.
      showConfirm: false,
      form: {
        wallet_type: 'FREIGHT',
        amount: null,
        description: '',
      },
      selectedFile: null,
      previewUrl: null,
    };
  },
  computed: {
    isFormValid() {
      return (
          this.form.wallet_type &&
          this.form.amount >= MIN_TOPUP_AMOUNT &&
          this.form.amount <= MAX_TOPUP_AMOUNT &&
          this.form.description.trim().length > 0
      );
    },
    // [4.2] true when an amount is entered but exceeds the client ceiling — drives an inline warning.
    isAmountOverCeiling() {
      return this.form.amount != null && this.form.amount > MAX_TOPUP_AMOUNT;
    },
    maxTopupAmount() {
      return MAX_TOPUP_AMOUNT;
    },
    isImageFile() {
      if (!this.selectedFile) return false;
      return this.selectedFile.type.startsWith('image/');
    },
  },
  watch: {
    // [B1] A change to any field that defines the money-in intent rotates the key, so a corrected
    // (genuinely different) top-up is NOT deduped against the prior attempt. Pure resubmits of the
    // same form leave the key untouched → Go dedups → exactly-once credit.
    'form.amount'() {
      this.rotateIdempotencyKey();
    },
    'form.wallet_type'() {
      this.rotateIdempotencyKey();
    },
    selectedFile() {
      this.rotateIdempotencyKey();
    },
  },
  methods: {
    rotateIdempotencyKey() {
      // [B1] Never rotate while a submit is in flight — a mid-flight intent change must not strip the
      // in-flight request's retry-dedup key (that is the exact double-credit scenario B1 prevents).
      // The intent inputs are also disabled during submit; this is the correctness backstop.
      if (this.isSubmitting) return;
      this.idempotencyKey = `topup-${crypto.randomUUID()}`;
      // Any intent change invalidates a staged confirmation.
      this.showConfirm = false;
    },
    close() {
      this.isOpen = false;
      this.$emit('close');
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate MIME type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ປະເພດໄຟລ໌ບໍ່ຖືກຕ້ອງ (ຮອງຮັບ: JPG, PNG, GIF, PDF)');
        event.target.value = '';
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ໄຟລ໌ໃຫຍ່ເກີນໄປ (ສູງສຸດ 5MB)');
        return;
      }

      this.selectedFile = file;

      // Create preview for images
      if (file.type.startsWith('image/')) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = 'pdf';
      }
    },
    clearFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },
    formatNumberInput,
    onAmountInput(value) {
      if (!value) {
        this.form.amount = null;
        return;
      }
      const cleaned = String(value).replace(/[^\d]/g, '');
      this.form.amount = parseInt(cleaned, 10) || null;
    },
    formatCurrency(amount) {
      if (!amount) return '';
      return _formatCurrency(amount);
    },
    // [4.2] Step 1: validate, then stage the confirm panel. The Go call only fires from submit().
    requestConfirm() {
      if (!this.isFormValid) return;
      // Slip image is required for manual top-up
      if (!this.selectedFile) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ກະລຸນາອັບໂຫລດສະລິບການໂອນ');
        return;
      }
      this.submitError = null;
      this.submitErrorRequestId = null;
      this.showConfirm = true;
    },
    async submit() {
      // [4.2] The money call is unreachable without an explicit confirmation stage, regardless of how
      // the component is wired in the future (defense beyond the template's two-step footer).
      if (!this.showConfirm) return;
      if (!this.isFormValid) return;

      // Slip image is required for manual top-up
      if (!this.selectedFile) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ກະລຸນາອັບໂຫລດສະລິບການໂອນ');
        return;
      }

      this.isSubmitting = true;
      this.submitError = null;
      this.submitErrorRequestId = null;

      try {
        const result = await walletService.manualTopUp(
          this.$axios,
          this.branch.id,
          this.form.wallet_type,
          this.form.amount,
          this.form.description,
          this.selectedFile,
          this.idempotencyKey // [B1] stable per-intent key (reused across resubmits)
        );

        if (result?.success) {
          // [C37] is_duplicate and balance_after live under result.data (same envelope Pending-Topups.vue
          // reads). result?.is_duplicate at the top level was always undefined, so a Go-deduped manual top-up
          // silently showed the normal success toast instead of the "already processed" warning. Read both
          // from the same `data` object so they can never drift.
          const data = result?.data || result;
          if (data?.is_duplicate) {
            this.$notification.OpenNotification_Error('top-right', 'warning', 5000, 'ລາຍການນີ້ຖືກດຳເນີນການແລ້ວ');
          } else {
            this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
          }
          this.$emit('success', {
            branchId: this.branch.id,
            walletType: this.form.wallet_type,
            balanceAfter: data?.balance_after,
          });
          this.close();
        } else {
          throw new Error(result?.message || 'ບໍ່ສາມາດເຕີມເງິນໄດ້');
        }
      } catch (error) {
        console.error('Top-up error:', error.response?.data || error);
        const { message, requestId } = extractErrorInfo(error);
        this.submitError = message || 'ບໍ່ສາມາດເຕີມເງິນໄດ້';
        this.submitErrorRequestId = requestId;
        this.isSubmitting = false;
        // [4.2/B1] Return to the form so the error is visible. The SAME idempotencyKey is retained,
        // so a resubmit of the unchanged form is deduped by Go (exactly-once credit).
        this.showConfirm = false;
      }
    },
  },
  beforeDestroy() {
    if (this.previewUrl && this.previewUrl !== 'pdf') {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #28a745;
  }
}

.dialog-content {
  padding: 20px;
}

.branch-info {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;

  .label {
    color: #666;
    margin-right: 8px;
  }

  .value {
    font-weight: 600;
    color: #333;
  }
}

.form-group {
  margin-bottom: 16px;

  .label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
  }

  .required {
    color: #dc3545;
  }

  .input,
  .textarea,
  .select select {
    width: 100%;
  }

  .help-text {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }
}

.file-upload {
  display: flex;
  align-items: center;
  gap: 12px;

  .file-name {
    color: #28a745;
    font-size: 14px;
  }
}

.slip-preview {
  margin-top: 16px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 8px;
    border: 1px solid #ddd;
  }

  .pdf-indicator {
    padding: 40px;
    background: #f8f9fa;
    border-radius: 8px;
    color: #dc3545;

    i {
      font-size: 48px;
      display: block;
      margin-bottom: 8px;
    }
  }
}

.submit-error {
  margin-top: 16px;

  .error-ref {
    font-size: 12px;
    color: #856404;
    margin-top: 4px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
